import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSettings } from '@/hooks/useSettings'
import { DEFAULT_PROFILE } from '@/hooks/useAgents'
import { getTelegramStatus, updateTelegramSettings, sendTelegramTest, getDiscordStatus, updateDiscordSettings, sendDiscordTest, testAlertPipeline } from '@/lib/api'
import {
  User, Bell, Shield, Palette, Database, Cpu, TrendingUp,
  ChevronRight, CheckCircle, AlertTriangle, RotateCcw,
  Trash2, Wifi, WifiOff, Loader2, Plug, Send,
} from 'lucide-react'
import type { TelegramStatus, DiscordStatus } from '@/lib/types'
import PageHeader from '@/components/ui/PageHeader'
import HouseholdAccessAdmin from '@/components/HouseholdAccessAdmin'
import type {
  PlatformSettings,
  GeneralSettings,
  TradingSettings,
  AgentSettings,
  NotificationSettings,
} from '@/lib/types'

// ─────────────────────────────────────────────────────────────────────────────
// Reusable components
// ─────────────────────────────────────────────────────────────────────────────

function Section({ title, icon: Icon, badge, children }: {
  title: string; icon: React.ElementType; badge?: string; children: React.ReactNode
}) {
  return (
    <section className="card overflow-hidden" aria-labelledby={`section-${title}`}>
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <Icon size={15} className="text-text-muted" aria-hidden />
        <h2 id={`section-${title}`} className="panel-title">{title}</h2>
        {badge && (
          <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-surface-hover text-text-muted">
            {badge}
          </span>
        )}
      </div>
      <div className="divide-y divide-border">{children}</div>
    </section>
  )
}

function Toggle({ checked, onChange, label, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-label={`Toggle ${label}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors duration-200 shrink-0 focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card disabled:opacity-40 ${checked ? 'bg-blue' : 'bg-surface-hover'}`}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-4' : ''}`}
        aria-hidden
      />
    </button>
  )
}

function SettingRow({ label, description, children }: {
  label: string; description?: string; children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 gap-4">
      <div className="min-w-0">
        <p className="text-sm text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-muted mt-0.5">{description}</p>}
      </div>
      <div className="shrink-0 flex items-center gap-2">{children}</div>
    </div>
  )
}

function SelectInput({ value, options, onChange, label }: {
  value: string; options: { value: string; label: string }[]; onChange: (v: string) => void; label?: string
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      title={label}
      aria-label={label}
      className="input w-auto px-2.5 py-1.5"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function NumberInput({ value, onChange, min, max, step, suffix, label }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; suffix?: string; label?: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        title={label}
        aria-label={label}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        className="input w-24 px-2.5 py-1.5 text-right"
      />
      {suffix && <span className="text-xs text-text-muted">{suffix}</span>}
    </div>
  )
}

function TextInput({ value, onChange, label }: {
  value: string; onChange: (v: string) => void; label?: string
}) {
  return (
    <input
      type="text"
      value={value}
      title={label}
      aria-label={label}
      onChange={e => onChange(e.target.value)}
      className="input w-48 px-2.5 py-1.5"
    />
  )
}

function SliderInput({ value, onChange, min, max, step, formatLabel, label }: {
  value: number; onChange: (v: number) => void; min: number; max: number; step: number
  formatLabel?: (v: number) => string; label?: string
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        title={label}
        aria-label={label}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-24 accent-blue"
      />
      <span className="text-sm text-text-secondary font-mono min-w-[4ch] text-right">
        {formatLabel ? formatLabel(value) : value}
      </span>
    </div>
  )
}

function ConnectorRow({ name, connected, provider }: {
  name: string; connected: boolean; provider: string
}) {
  return (
    <SettingRow
      label={name}
      description={connected ? `Connected to ${provider}` : 'Not connected'}
    >
      <span className={`flex items-center gap-1.5 text-xs font-medium ${connected ? 'text-green' : 'text-text-muted'}`}>
        {connected ? <Wifi size={12} /> : <WifiOff size={12} />}
        {connected ? 'Connected' : 'Disconnected'}
      </span>
      <button className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${connected
        ? 'border-red/30 text-red hover:bg-red/10'
        : 'border-blue/30 text-blue hover:bg-blue/10'
      }`}>
        {connected ? 'Disconnect' : 'Connect'}
      </button>
    </SettingRow>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const MODEL_OPTIONS = [
  { value: 'qwen-plus', label: 'Qwen Plus' },
  { value: 'qwen-turbo', label: 'Qwen Turbo' },
  { value: 'qwen-max', label: 'Qwen Max' },
  { value: 'deepseek-chat', label: 'DeepSeek V3' },
  { value: 'deepseek-reasoner', label: 'DeepSeek R1' },
]

const CURRENCY_OPTIONS = [
  { value: 'GBP', label: 'GBP' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
]

const TIMEZONE_OPTIONS = [
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
]

const AGENT_LABELS: Record<string, string> = {
  aria: 'ARIA (Orchestrator)',
  market: 'Market Intelligence',
  screener: 'Stock Screener',
  portfolio: 'Portfolio Optimizer',
  risk: 'Risk Manager',
  goals: 'Goals Planner',
  compliance: 'Compliance',
}

export default function SettingsPage() {
  const { user, logout } = useAuth()
  const { settings, loading, saving, error, fetchSettings, updateSettings, updateAgentSettings, updatePotRules, resetToDefaults } = useSettings()

  const [local, setLocal] = useState<PlatformSettings | null>(null)
  const [saved, setSaved] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [showLiveWarning, setShowLiveWarning] = useState(false)

  // Telegram state
  const [tg, setTg] = useState<TelegramStatus | null>(null)
  const [tgToken, setTgToken] = useState('')
  const [tgChatId, setTgChatId] = useState('')
  const [tgSending, setTgSending] = useState(false)
  const [tgTestResult, setTgTestResult] = useState<string | null>(null)

  // Discord state
  const [dc, setDc] = useState<DiscordStatus | null>(null)
  const [dcWebhookUrl, setDcWebhookUrl] = useState('')
  const [dcSending, setDcSending] = useState(false)
  const [dcTestResult, setDcTestResult] = useState<string | null>(null)
  const [alertTesting, setAlertTesting] = useState(false)
  const [alertTestResult, setAlertTestResult] = useState<string | null>(null)

  useEffect(() => { fetchSettings() }, [fetchSettings])
  useEffect(() => { if (settings) setLocal(settings) }, [settings])

  // Fetch Telegram & Discord status on mount
  useEffect(() => {
    getTelegramStatus().then(setTg).catch(() => {})
    getDiscordStatus().then(setDc).catch(() => {})
  }, [])

  const handleTgSave = useCallback(async () => {
    const updates: Record<string, unknown> = {}
    if (tgToken) updates.bot_token = tgToken
    if (tgChatId) updates.chat_id = tgChatId
    const result = await updateTelegramSettings(updates)
    setTg(result)
    setTgToken('')
    setTgChatId('')
  }, [tgToken, tgChatId])

  const handleTgToggle = useCallback(async (key: string, value: boolean) => {
    const result = await updateTelegramSettings({ [key]: value })
    setTg(result)
  }, [])

  const handleTgTest = useCallback(async () => {
    setTgSending(true)
    setTgTestResult(null)
    try {
      const result = await sendTelegramTest()
      setTgTestResult(result.ok ? 'Message sent!' : (result.error || 'Failed'))
    } catch {
      setTgTestResult('Failed to send test message')
    }
    setTgSending(false)
    setTimeout(() => setTgTestResult(null), 4000)
  }, [])

  const handleDcSave = useCallback(async () => {
    const updates: Record<string, unknown> = {}
    if (dcWebhookUrl) updates.webhook_url = dcWebhookUrl
    const result = await updateDiscordSettings(updates)
    setDc(result)
    setDcWebhookUrl('')
  }, [dcWebhookUrl])

  const handleDcToggle = useCallback(async (key: string, value: boolean) => {
    const result = await updateDiscordSettings({ [key]: value })
    setDc(result)
  }, [])

  const handleDcTest = useCallback(async () => {
    setDcSending(true)
    setDcTestResult(null)
    try {
      const result = await sendDiscordTest()
      setDcTestResult(result.ok ? 'Message sent!' : (result.error || 'Failed'))
    } catch {
      setDcTestResult('Failed to send test message')
    }
    setDcSending(false)
    setTimeout(() => setDcTestResult(null), 4000)
  }, [])

  const handleAlertTest = useCallback(async () => {
    setAlertTesting(true)
    setAlertTestResult(null)
    try {
      const result = await testAlertPipeline()
      const parts: string[] = ['In-app: created']
      if (result.telegram) parts.push('Telegram: sent')
      else if (result.errors.telegram) parts.push(`Telegram: ${result.errors.telegram}`)
      if (result.discord) parts.push('Discord: sent')
      else if (result.errors.discord) parts.push(`Discord: ${result.errors.discord}`)
      setAlertTestResult(parts.join(' | '))
    } catch {
      setAlertTestResult('Failed to send test alert')
    }
    setAlertTesting(false)
    setTimeout(() => setAlertTestResult(null), 6000)
  }, [])

  const dirty = local && settings && JSON.stringify(local) !== JSON.stringify(settings)

  const update = useCallback(<K extends keyof PlatformSettings>(
    section: K,
    key: string,
    value: unknown,
  ) => {
    setLocal(prev => {
      if (!prev) return prev
      return { ...prev, [section]: { ...prev[section], [key]: value } }
    })
  }, [])

  const updateModel = useCallback((tier: string, model: string) => {
    setLocal(prev => {
      if (!prev) return prev
      return {
        ...prev,
        agents: {
          ...prev.agents,
          models: { ...prev.agents.models, [tier]: model },
        },
      }
    })
  }, [])

  const updateAgentEnabled = useCallback((agent: string, enabled: boolean) => {
    setLocal(prev => {
      if (!prev) return prev
      return {
        ...prev,
        agents: {
          ...prev.agents,
          enabled: { ...prev.agents.enabled, [agent]: enabled },
        },
      }
    })
  }, [])

  const handleSave = async () => {
    if (!local) return

    // Save all sections
    const ok = await updateSettings({
      general: local.general,
      trading: local.trading,
      notifications: local.notifications,
      connectors: local.connectors,
    })
    if (ok) {
      await updateAgentSettings({
        schedule_interval_minutes: local.agents.schedule_interval_minutes,
        deep_analysis_interval_minutes: local.agents.deep_analysis_interval_minutes,
        models: local.agents.models,
        enabled: local.agents.enabled,
      })
    }
    if (ok) {
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
  }

  const handleReset = async () => {
    const ok = await resetToDefaults()
    if (ok) {
      setShowResetConfirm(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
  }

  const handlePaperToggle = (v: boolean) => {
    if (!v) {
      // Turning off paper mode -- show warning
      setShowLiveWarning(true)
    } else {
      update('trading', 'paper_mode', true)
    }
  }

  if (loading || !local) {
    return (
      <div className="space-y-6 animate-fade-in max-w-2xl">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-64" />
        <div className="skeleton h-48" />
      </div>
    )
  }

  const g = local.general
  const t = local.trading
  const a = local.agents
  const n = local.notifications
  const c = local.connectors

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <PageHeader
        title="Settings"
        subtitle="Configure your platform, agents, and trading rules"
        actions={dirty && (
          <span className="text-xs text-amber-400 flex items-center gap-1">
            <AlertTriangle size={12} />
            Unsaved changes
          </span>
        )}
      />

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red/10 border border-red/20 text-sm text-red">
          {error}
        </div>
      )}

      {/* ── General ──────────────────────────────────────────────────────── */}
      <Section title="General" icon={Palette}>
        <SettingRow label="Fund Name" description="Display name for your fund">
          <TextInput value={g.fund_name} onChange={v => update('general', 'fund_name', v)} label="Fund Name" />
        </SettingRow>
        <SettingRow label="Currency" description="Base reporting currency">
          <SelectInput value={g.currency} options={CURRENCY_OPTIONS} onChange={v => update('general', 'currency', v)} label="Currency" />
        </SettingRow>
        <SettingRow label="Timezone" description="Market hours reference">
          <SelectInput value={g.timezone} options={TIMEZONE_OPTIONS} onChange={v => update('general', 'timezone', v)} label="Timezone" />
        </SettingRow>
      </Section>

      {/* ── Trading Rules ────────────────────────────────────────────────── */}
      <Section title="Trading Rules" icon={TrendingUp} badge={t.paper_mode ? 'PAPER' : 'LIVE'}>
        <SettingRow
          label="Paper Trading Mode"
          description={t.paper_mode ? 'All trades are simulated -- no real money at risk' : 'LIVE MODE -- real money trades will execute'}
        >
          <Toggle checked={t.paper_mode} onChange={handlePaperToggle} label="Paper Mode" />
        </SettingRow>
        <SettingRow label="Require Human Approval" description="Queue trades for manual approval before execution">
          <Toggle checked={t.require_approval} onChange={v => update('trading', 'require_approval', v)} label="Require Approval" />
        </SettingRow>
        <SettingRow label="Max Pot Size" description="Maximum capital in the discretionary trading pot">
          <NumberInput value={t.max_pot_size} onChange={v => update('trading', 'max_pot_size', v)} min={100} step={500} suffix="GBP" label="Max Pot Size" />
        </SettingRow>
        <SettingRow label="Max Trade Size" description="Maximum single trade as % of pot">
          <SliderInput
            value={t.max_trade_pct}
            onChange={v => update('trading', 'max_trade_pct', v)}
            min={0.01} max={0.50} step={0.01}
            formatLabel={v => `${(v * 100).toFixed(0)}%`}
            label="Max Trade Size"
          />
        </SettingRow>
        <SettingRow label="Max Daily Drawdown" description="Pause trading if daily loss exceeds this">
          <SliderInput
            value={t.max_daily_drawdown}
            onChange={v => update('trading', 'max_daily_drawdown', v)}
            min={0.02} max={0.20} step={0.01}
            formatLabel={v => `${(v * 100).toFixed(0)}%`}
            label="Max Daily Drawdown"
          />
        </SettingRow>
        <SettingRow label="Max Total Drawdown" description="Full system pause if total drawdown exceeds this">
          <SliderInput
            value={t.max_total_drawdown}
            onChange={v => update('trading', 'max_total_drawdown', v)}
            min={0.05} max={0.50} step={0.01}
            formatLabel={v => `${(v * 100).toFixed(0)}%`}
            label="Max Total Drawdown"
          />
        </SettingRow>
        <SettingRow label="Max Open Positions" description="Limit concurrent open trades">
          <NumberInput value={t.max_positions} onChange={v => update('trading', 'max_positions', Math.max(1, Math.round(v)))} min={1} max={20} step={1} label="Max Positions" />
        </SettingRow>
      </Section>

      {/* ── Agent Configuration ──────────────────────────────────────────── */}
      <Section title="Agent Configuration" icon={Cpu} badge={`${Object.values(a.enabled).filter(Boolean).length}/${Object.keys(a.enabled).length} active`}>
        <SettingRow label="Analysis Schedule" description="How often agents run market scans">
          <NumberInput value={a.schedule_interval_minutes} onChange={v => update('agents', 'schedule_interval_minutes', Math.max(1, Math.round(v)))} min={1} max={120} step={1} suffix="min" label="Schedule Interval" />
        </SettingRow>
        <SettingRow label="Deep Analysis Interval" description="Full portfolio re-analysis frequency">
          <NumberInput value={a.deep_analysis_interval_minutes} onChange={v => update('agents', 'deep_analysis_interval_minutes', Math.max(5, Math.round(v)))} min={5} max={480} step={5} suffix="min" label="Deep Analysis Interval" />
        </SettingRow>
        <div className="px-5 py-3 border-b border-border">
          <p className="text-xs text-text-muted font-semibold uppercase tracking-wider mb-3">Model Selection</p>
          <div className="space-y-2.5">
            {(['fast', 'deep', 'cheap'] as const).map(tier => (
              <div key={tier} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary capitalize">{tier} tier</span>
                <SelectInput
                  value={a.models[tier]}
                  options={MODEL_OPTIONS}
                  onChange={v => updateModel(tier, v)}
                  label={`${tier} tier model`}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-3">
          <p className="text-xs text-text-muted font-semibold uppercase tracking-wider mb-3">Agent Toggles</p>
          <div className="space-y-2.5">
            {Object.entries(AGENT_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{label}</span>
                <Toggle
                  checked={a.enabled[key as keyof typeof a.enabled]}
                  onChange={v => updateAgentEnabled(key, v)}
                  label={label}
                  disabled={key === 'risk'}
                />
              </div>
            ))}
            <p className="text-xs text-text-muted mt-1">Risk Manager cannot be disabled (safety requirement).</p>
          </div>
        </div>
      </Section>

      {/* ── Notifications ────────────────────────────────────────────────── */}
      <Section title="Notifications" icon={Bell}>
        <SettingRow label="Email Notifications" description="Receive alerts via email">
          <Toggle checked={n.email_enabled} onChange={v => update('notifications', 'email_enabled', v)} label="Email" />
        </SettingRow>
        <SettingRow label="Push Notifications" description="In-app push alerts">
          <Toggle checked={n.push_enabled} onChange={v => update('notifications', 'push_enabled', v)} label="Push" />
        </SettingRow>
        <SettingRow label="Recommendation Alerts" description="Notify when agents generate new recommendations">
          <Toggle checked={n.alert_on_recommendation} onChange={v => update('notifications', 'alert_on_recommendation', v)} label="Recommendations" />
        </SettingRow>
        <SettingRow label="Trade Alerts" description="Notify on trade execution or rejection">
          <Toggle checked={n.alert_on_trade} onChange={v => update('notifications', 'alert_on_trade', v)} label="Trades" />
        </SettingRow>
        <SettingRow label="Risk Warnings" description="Alert on pot rule breaches or high-risk conditions">
          <Toggle checked={n.alert_on_risk_warning} onChange={v => update('notifications', 'alert_on_risk_warning', v)} label="Risk" />
        </SettingRow>
        {/* Telegram sub-section */}
        <div className="px-5 py-3 border-t border-border">
          <p className="text-xs text-text-muted font-semibold uppercase tracking-wider mb-3">Telegram Alerts</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Bot Token</p>
                <p className="text-xs text-text-muted">{tg?.bot_token_set ? 'Configured' : 'Not set'}</p>
              </div>
              <input
                type="password"
                value={tgToken}
                placeholder={tg?.bot_token_set ? '********' : 'Paste bot token'}
                onChange={e => setTgToken(e.target.value)}
                className="input w-48 px-2.5 py-1.5"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Chat ID</p>
                <p className="text-xs text-text-muted">{tg?.chat_id_set ? 'Configured' : 'Not set'}</p>
              </div>
              <input
                type="text"
                value={tgChatId}
                placeholder={tg?.chat_id_set ? '********' : 'Your chat ID'}
                onChange={e => setTgChatId(e.target.value)}
                className="input w-48 px-2.5 py-1.5"
              />
            </div>
            {(tgToken || tgChatId) && (
              <button type="button" onClick={handleTgSave} className="text-xs px-3 py-1.5 rounded-md border border-blue/30 text-blue hover:bg-blue/10 transition-colors">
                Save Telegram Credentials
              </button>
            )}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleTgTest}
                disabled={tgSending || !tg?.configured}
                className="text-xs px-3 py-1.5 rounded-md border border-blue/30 text-blue hover:bg-blue/10 transition-colors flex items-center gap-1.5 disabled:opacity-40"
              >
                {tgSending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Send Test Message
              </button>
              {tgTestResult && (
                <span className={`text-xs ${tgTestResult.includes('sent') ? 'text-green' : 'text-red'}`}>
                  {tgTestResult}
                </span>
              )}
            </div>
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Recommendations</span>
                <Toggle checked={tg?.forward_recommendations ?? true} onChange={v => handleTgToggle('forward_recommendations', v)} label="Telegram Recommendations" disabled={!tg?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Trade Executions</span>
                <Toggle checked={tg?.forward_trades ?? true} onChange={v => handleTgToggle('forward_trades', v)} label="Telegram Trades" disabled={!tg?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Risk Warnings</span>
                <Toggle checked={tg?.forward_risk ?? true} onChange={v => handleTgToggle('forward_risk', v)} label="Telegram Risk" disabled={!tg?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Watchlist Alerts</span>
                <Toggle checked={tg?.forward_watchlist ?? true} onChange={v => handleTgToggle('forward_watchlist', v)} label="Telegram Watchlist" disabled={!tg?.configured} />
              </div>
            </div>
          </div>
        </div>
        {/* Discord sub-section */}
        <div className="px-5 py-3 border-t border-border">
          <p className="text-xs text-text-muted font-semibold uppercase tracking-wider mb-3">Discord Alerts</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-primary">Webhook URL</p>
                <p className="text-xs text-text-muted">{dc?.webhook_url_set ? 'Configured' : 'Not set'}</p>
              </div>
              <input
                type="password"
                value={dcWebhookUrl}
                placeholder={dc?.webhook_url_set ? '********' : 'Paste webhook URL'}
                onChange={e => setDcWebhookUrl(e.target.value)}
                className="input w-48 px-2.5 py-1.5"
              />
            </div>
            {dcWebhookUrl && (
              <button type="button" onClick={handleDcSave} className="text-xs px-3 py-1.5 rounded-md border border-blue/30 text-blue hover:bg-blue/10 transition-colors">
                Save Discord Webhook
              </button>
            )}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleDcTest}
                disabled={dcSending || !dc?.configured}
                className="text-xs px-3 py-1.5 rounded-md border border-blue/30 text-blue hover:bg-blue/10 transition-colors flex items-center gap-1.5 disabled:opacity-40"
              >
                {dcSending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Send Test Message
              </button>
              {dcTestResult && (
                <span className={`text-xs ${dcTestResult.includes('sent') ? 'text-green' : 'text-red'}`}>
                  {dcTestResult}
                </span>
              )}
            </div>
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Recommendations</span>
                <Toggle checked={dc?.forward_recommendations ?? true} onChange={v => handleDcToggle('forward_recommendations', v)} label="Discord Recommendations" disabled={!dc?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Trade Executions</span>
                <Toggle checked={dc?.forward_trades ?? true} onChange={v => handleDcToggle('forward_trades', v)} label="Discord Trades" disabled={!dc?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Risk Warnings</span>
                <Toggle checked={dc?.forward_risk ?? true} onChange={v => handleDcToggle('forward_risk', v)} label="Discord Risk" disabled={!dc?.configured} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Watchlist Alerts</span>
                <Toggle checked={dc?.forward_watchlist ?? true} onChange={v => handleDcToggle('forward_watchlist', v)} label="Discord Watchlist" disabled={!dc?.configured} />
              </div>
            </div>
          </div>
        </div>
        {/* Full-pipeline alert test */}
        <div className="px-5 py-3 border-t border-border">
          <p className="text-xs text-text-muted font-semibold uppercase tracking-wider mb-3">Alert Pipeline Test</p>
          <p className="text-xs text-text-muted mb-3">
            Fires a test alert through all configured channels at once (in-app notification, Telegram, Discord).
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAlertTest}
              disabled={alertTesting}
              className="text-xs px-3 py-1.5 rounded-md border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors flex items-center gap-1.5 disabled:opacity-40"
            >
              {alertTesting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Test Alerts
            </button>
            {alertTestResult && (
              <span className={`text-xs ${alertTestResult.includes('Failed') ? 'text-red' : 'text-green'}`}>
                {alertTestResult}
              </span>
            )}
          </div>
        </div>
      </Section>

      {/* ── Connectors ───────────────────────────────────────────────────── */}
      <Section title="Broker Connectors" icon={Plug}>
        <ConnectorRow name="IG Index" connected={c.ig_connected} provider="IG Group" />
        <ConnectorRow name="Robinhood" connected={c.robinhood_connected} provider="Robinhood Markets" />
        <ConnectorRow name="Interactive Brokers" connected={c.ibkr_connected} provider="IBKR (Paper)" />
      </Section>

      {/* ── Household & access (owner-only; renders nothing otherwise) ──── */}
      <HouseholdAccessAdmin />

      {/* ── Account ──────────────────────────────────────────────────────── */}
      <Section title="Account" icon={User}>
        <div className="px-5 py-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-grad-blue flex items-center justify-center text-white font-bold text-lg shrink-0">
            {user?.name?.charAt(0) ?? 'U'}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-text-primary">{user?.name ?? DEFAULT_PROFILE.name}</p>
            <p className="text-sm text-text-secondary">{user?.title ?? 'Founder & CTO'} &middot; {user?.company ?? 'Caiborg AI'}</p>
            <p className="text-xs text-text-muted mt-0.5">{user?.email}</p>
          </div>
        </div>
        <SettingRow label="Two-Factor Authentication" description="TOTP authenticator app">
          <span className="text-xs text-green flex items-center gap-1">
            <CheckCircle size={12} /> Enabled
          </span>
          <a href="/mfa" className="text-xs text-blue hover:underline">
            Manage <ChevronRight size={12} className="inline" />
          </a>
        </SettingRow>
        <SettingRow label="Session Timeout" description="Auto-logout after inactivity">
          <span className="text-sm text-text-secondary">30 min</span>
        </SettingRow>
        <div className="px-5 py-3.5">
          <button onClick={logout} className="btn-secondary text-sm">
            Log Out
          </button>
        </div>
      </Section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <Section title="Security & Data" icon={Shield}>
        <SettingRow label="Password" description="Change the password used to log in">
          <a
            href="/app/change-password"
            className="text-xs text-blue hover:underline"
          >
            Change password
          </a>
        </SettingRow>
        <SettingRow label="Audit Log" description="All agent decisions and trades are logged">
          <span className="text-xs text-green flex items-center gap-1">
            <CheckCircle size={12} /> Enabled
          </span>
        </SettingRow>
        <SettingRow label="MPC Encryption" description="Homomorphic encryption for computations">
          <span className="text-xs text-text-muted">Coming soon</span>
        </SettingRow>
        <SettingRow label="Data Source" description="Portfolio data provider">
          <span className="text-sm text-text-secondary flex items-center gap-1">
            MCP stub (demo) <ChevronRight size={14} className="text-text-muted" />
          </span>
        </SettingRow>
        <SettingRow label="Export Portfolio" description="Download holdings as CSV">
          <button className="text-xs text-blue hover:underline">Export</button>
        </SettingRow>
      </Section>

      {/* ── Danger Zone ──────────────────────────────────────────────────── */}
      <Section title="Danger Zone" icon={AlertTriangle}>
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-primary">Reset All Settings</p>
              <p className="text-xs text-text-muted">Restore all settings to factory defaults</p>
            </div>
            <button
              onClick={() => setShowResetConfirm(true)}
              className="text-xs px-3 py-1.5 rounded-md border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors flex items-center gap-1.5"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-primary">Clear Trade History</p>
              <p className="text-xs text-text-muted">Permanently delete all simulated and executed trade records</p>
            </div>
            <button
              onClick={() => setShowClearConfirm(true)}
              className="text-xs px-3 py-1.5 rounded-md border border-red/30 text-red hover:bg-red/10 transition-colors flex items-center gap-1.5"
            >
              <Trash2 size={12} /> Clear
            </button>
          </div>
        </div>
      </Section>

      {/* ── Save bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 sticky bottom-4">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="btn-primary gap-2 disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle size={14} /> : null}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
        <button
          onClick={() => setLocal(settings)}
          disabled={!dirty}
          className="btn-secondary disabled:opacity-40"
        >
          Cancel
        </button>
      </div>

      {/* ── Reset Confirmation Modal ─────────────────────────────────────── */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in" onClick={() => setShowResetConfirm(false)}>
          <div className="card p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-400" />
              <h3 className="font-semibold text-text-primary">Reset Settings?</h3>
            </div>
            <p className="text-sm text-text-secondary mb-5">
              This will restore all settings to their factory defaults. Your trade history and portfolio data will not be affected.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowResetConfirm(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleReset} className="text-sm px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors">
                Reset All Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clear Trade History Confirmation ──────────────────────────────── */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in" onClick={() => setShowClearConfirm(false)}>
          <div className="card p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={18} className="text-red" />
              <h3 className="font-semibold text-text-primary">Clear Trade History?</h3>
            </div>
            <p className="text-sm text-text-secondary mb-5">
              This will permanently delete all trade records including paper trades. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowClearConfirm(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={() => setShowClearConfirm(false)} className="text-sm px-4 py-2 rounded-lg bg-red text-white hover:opacity-90 transition-colors">
                Clear History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Live Mode Warning ────────────────────────────────────────────── */}
      {showLiveWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in" onClick={() => setShowLiveWarning(false)}>
          <div className="card border-red/30 p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red" />
              <h3 className="font-semibold text-text-primary">Switch to Live Trading?</h3>
            </div>
            <p className="text-sm text-text-secondary mb-2">
              Disabling paper mode means trades will execute with <strong className="text-text-primary">real money</strong>.
              Make sure you have:
            </p>
            <ul className="text-sm text-text-secondary mb-5 list-disc pl-5 space-y-1">
              <li>Connected a live broker account</li>
              <li>Completed at least 20 paper trades</li>
              <li>Reviewed all risk limits above</li>
            </ul>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowLiveWarning(false)} className="btn-secondary text-sm">Stay Paper</button>
              <button
                onClick={() => { update('trading', 'paper_mode', false); setShowLiveWarning(false) }}
                className="text-sm px-4 py-2 rounded-lg bg-red text-white hover:opacity-90 transition-colors"
              >
                Go Live
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
